import { Controller } from "@hotwired/stimulus";

// GR-3759: repeating concession rows on the quote form, the same shape as the
// line items above them (quote_line_items_controller.js). Rows are
// server-rendered and the blank one lives in a <template>, so with JS disabled
// every concession already on the quote is still editable; only adding and
// removing need this controller.
//
// It also narrows each row to the one value field its kind is measured in. That
// is presentation and nothing else: with JS off all four fields are visible and
// named for their units, and the model drops the ones the chosen kind does not
// use, so no save depends on what is on screen here.
export default class QuoteConcessionsController extends Controller {
  static targets = ["rows", "row", "template"];

  // kind -> the name of the field that kind's value is posted in, handed over
  // by the view so the mapping lives in the model rather than twice.
  static values = { fields: Object };

  connect() {
    this.rowTargets.forEach((row) => this.showValueField(row));
  }

  addRow() {
    const html = this.templateTarget.innerHTML.replace(
      /__INDEX__/g,
      String(this.nextIndex())
    );
    this.rowsTarget.insertAdjacentHTML("beforeend", html);
    this.renumber();
    this.showValueField(this.rowsTarget.lastElementChild);
  }

  kindChanged(event) {
    const row = event.target.closest("[data-quote-concessions-target='row']");
    if (row) this.showValueField(row);
  }

  // A saved concession is marked for destruction and hidden, so the server is
  // told about the deletion. An unsaved row just leaves the DOM.
  removeRow(event) {
    const row = event.target.closest("[data-quote-concessions-target='row']");
    if (!row) return;

    if (row.querySelector("input[name$='[id]']")) {
      row.querySelector("input[name$='[_destroy]']").value = "1";
      row.hidden = true;
    } else {
      row.remove();
    }
    this.renumber();
  }

  // Where each concession sits in the list, which is a different number from
  // the params index above: an index only has to be unique inside the post, and
  // keeps counting past rows that have been removed, so a row taking its index
  // as its position landed on one a server-rendered row already held.
  //
  // Written across the rows on screen rather than onto the row that moved,
  // because after a removal the ones below it have moved too. A row on its way
  // out takes no position in a list it is leaving; its own is left alone, since
  // the server is about to destroy it.
  renumber() {
    let position = 0;
    this.rowTargets.forEach((row) => {
      if (row.hidden) return;

      const field = row.querySelector("input[name$='[position]']");
      if (field) field.value = String(++position);
    });
  }

  // An unrecognized kind leaves every field visible rather than hiding all of
  // them, so a kind added to the enum without being added to the mapping asks
  // for too much rather than for nothing.
  showValueField(row) {
    if (!row) return;

    const kind = row.querySelector("select[name$='[kind]']")?.value;
    const wanted = this.fieldsValue[kind];
    row.querySelectorAll("[data-concession-value]").forEach((cell) => {
      cell.hidden = wanted !== undefined && cell.dataset.concessionValue !== wanted;
    });
  }

  // Index only has to be unique within the submitted params, so keep counting
  // up past rows that have been removed.
  nextIndex() {
    this.rowCounter = Math.max(this.rowCounter ?? 0, this.rowTargets.length);
    return this.rowCounter++;
  }
}
